import { Box, Typography, CircularProgress, Button, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import type { TranslationKey } from '@finance/contracts';
import { translateRuntimeTKeyOrFallback } from '../../i18n/resolve-key';
import { useAppI18n } from '../../i18n/use-app-i18n';

export interface PageStateProps {
  titleKey?: TranslationKey;
  messageKey?: TranslationKey;
}

interface StateSurfaceProps {
  state: string;
  title: string;
  message?: string;
  tone: 'info' | 'warning' | 'error';
  children?: ReactNode;
}

function StateSurface({ state, title, message, tone, children }: StateSurfaceProps) {
  const borderColor = tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.main' : 'info.main';
  return (
    <Box
      role={tone === 'error' ? 'alert' : 'status'}
      data-state={state}
      sx={{
        width: '100%',
        maxWidth: 760,
        mx: 'auto',
        my: { xs: 2, md: 5 },
        p: { xs: 3, sm: 4, md: 5 },
        border: 1,
        borderLeft: 4,
        borderColor: 'divider',
        borderLeftColor: borderColor,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>{title}</Typography>
        {message && (
          <Typography color="text.secondary" sx={{ maxWidth: '65ch', lineHeight: 1.65 }}>
            {message}
          </Typography>
        )}
        {children}
      </Stack>
    </Box>
  );
}

function usePageStateContent(titleKey: TranslationKey, messageKey: TranslationKey) {
  const i18n = useAppI18n();
  return {
    title: i18n.text(titleKey),
    message: i18n.text(messageKey),
    ...i18n,
  };
}

function RemediationText({ remediation }: { remediation?: { action: string; routeId?: string } }) {
  const { optionalRuntimeText } = useAppI18n();
  if (!remediation) return null;
  const content = optionalRuntimeText(`navigation.remediation.${remediation.action}`);
  if (!content) return null;
  return <Typography variant="body2" color="text.secondary">{content}</Typography>;
}

export function PageLoadingState() {
  const { text } = useAppI18n();
  return (
    <Box
      role="status"
      aria-label={text('common.loading')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
      }}
    >
      <CircularProgress aria-hidden="true" />
    </Box>
  );
}

export function PageEmptyState({
  titleKey = 'common.states.empty.title',
  messageKey = 'common.states.empty.message',
  actions,
}: PageStateProps & { actions?: ReactNode }) {
  const { title, message } = usePageStateContent(titleKey, messageKey);
  return (
    <StateSurface state="empty" title={title} message={message} tone="info">
      {actions && <Stack direction="row" spacing={1} sx={{ mt: 1 }}>{actions}</Stack>}
    </StateSurface>
  );
}

export function PageErrorState({
  titleKey = 'common.states.error.title',
  messageKey = 'common.states.error.message',
  reasonCode,
  onRetry,
}: PageStateProps & { reasonCode?: string; onRetry?: () => void }) {
  const { title, message, text, optionalRuntimeText } = usePageStateContent(titleKey, messageKey);
  const reasonText = optionalRuntimeText(reasonCode);
  return (
    <StateSurface state="error" title={title} message={message} tone="error">
      {reasonCode && <span data-reason-code={reasonCode} />}
      {reasonText && <Typography variant="body2" color="error.main">{reasonText}</Typography>}
      {onRetry && (
        <Box><Button variant="contained" onClick={onRetry}>{text('common.retry')}</Button></Box>
      )}
    </StateSurface>
  );
}

export function PageForbiddenState({
  titleKey = 'common.states.forbidden.title',
  messageKey = 'common.states.forbidden.message',
  reasonCode = 'AUTH_FORBIDDEN',
  remediation,
  decisionId,
}: PageStateProps & {
  reasonCode?: string;
  remediation?: { action: string; routeId?: string };
  decisionId?: string;
}) {
  const { title, message, optionalRuntimeText } = usePageStateContent(titleKey, messageKey);
  const reasonText = optionalRuntimeText(reasonCode);
  return (
    <StateSurface state="forbidden" title={title} message={message} tone="error">
      <span data-reason-code={reasonCode} />
      {decisionId && <span data-decision-id={decisionId} />}
      {reasonText && <Typography variant="body2" color="error.main">{reasonText}</Typography>}
      <RemediationText remediation={remediation} />
    </StateSurface>
  );
}

export function PageNotFoundState({
  titleKey = 'common.states.notFound.title',
  messageKey = 'common.states.notFound.message',
}: PageStateProps) {
  const { title, message } = usePageStateContent(titleKey, messageKey);
  return <StateSurface state="not-found" title={title} message={message} tone="warning" />;
}

export function PageDegradedState({
  titleKey = 'common.states.degraded.title',
  messageKey = 'common.states.degraded.message',
  reasonCode,
}: PageStateProps & { reasonCode?: string }) {
  const { title, message } = usePageStateContent(titleKey, messageKey);
  return (
    <StateSurface state="degraded" title={title} message={message} tone="warning">
      {reasonCode && <span data-reason-code={reasonCode} />}
    </StateSurface>
  );
}

export function PageStaleState({
  titleKey = 'common.states.stale.title',
  messageKey = 'common.states.stale.message',
  asOfDate,
}: PageStateProps & { asOfDate?: string }) {
  const { title, message } = usePageStateContent(titleKey, messageKey);
  return (
    <StateSurface state="stale" title={title} message={message} tone="warning">
      {asOfDate && <span data-as-of-date={asOfDate} />}
    </StateSurface>
  );
}

interface RouteStateProps {
  routeId: string;
  disabledReasonKey?: string;
  remediation?: { action: string; routeId?: string };
  decisionId?: string;
}

function RouteAvailabilityState({
  routeId,
  disabledReasonKey,
  remediation,
  decisionId,
  state,
  titleKey,
  messageKey,
  tone,
}: RouteStateProps & {
  state: 'planned' | 'maintenance' | 'unavailable';
  titleKey: TranslationKey;
  messageKey: TranslationKey;
  tone: 'info' | 'warning';
}) {
  const { title, message, text, translator } = usePageStateContent(titleKey, messageKey);
  const reasonText = disabledReasonKey
    ? translateRuntimeTKeyOrFallback(translator, disabledReasonKey)
    : undefined;

  return (
    <Box data-route-id={routeId} data-disabled-reason-key={disabledReasonKey}>
      <StateSurface state={state} title={title} message={message} tone={tone}>
        {decisionId && <span data-decision-id={decisionId} />}
        {reasonText && reasonText !== message && (
          <Typography variant="body2" color="text.secondary">{reasonText}</Typography>
        )}
        <RemediationText remediation={remediation} />
      </StateSurface>
    </Box>
  );
}

export function PagePlannedState(props: RouteStateProps) {
  return (
    <RouteAvailabilityState
      {...props}
      state="planned"
      titleKey="common.states.planned.title"
      messageKey="common.states.planned.message"
      tone="info"
    />
  );
}

export function PageMaintenanceState(props: RouteStateProps) {
  return (
    <RouteAvailabilityState
      {...props}
      state="maintenance"
      titleKey="common.states.maintenance.title"
      messageKey="common.states.maintenance.message"
      tone="warning"
    />
  );
}

export function PageUnavailableState(props: RouteStateProps) {
  return (
    <RouteAvailabilityState
      {...props}
      state="unavailable"
      titleKey="common.states.unavailable.title"
      messageKey="common.states.unavailable.message"
      tone="warning"
    />
  );
}

export { LoadingState, type LoadingStateProps } from './LoadingState';
export { EmptyState, type EmptyStateProps } from './EmptyState';
export { ErrorState, type ErrorStateProps } from './ErrorState';
export { ForbiddenState, type ForbiddenStateProps } from './ForbiddenState';
export { NotFoundState, type NotFoundStateProps } from './NotFoundState';
export { DegradedState, type DegradedStateProps } from './DegradedState';
